import AsyncStorage from '@react-native-async-storage/async-storage';
import { supabase } from '@/lib/supabase';
import { persistentContentCache } from './persistentContentCacheService';

const LOCAL_VERSION_KEY = '@content_cache_version_global';
const LAST_REMOTE_CHECK_KEY = '@last_remote_version_check';
const REMOTE_CHECK_INTERVAL_MS = 24 * 60 * 60 * 1000;
const REMOTE_VERSION_SHORT_TTL_MS = 60 * 1000;

interface CacheVersion {
  timestamp: string;
  localTimestamp: number;
}

interface RemoteVersionCache {
  version: string | null;
  timestamp: number;
}

class CacheVersionService {
  private versionCache: CacheVersion | null = null;
  private lastRemoteCheckCache: number | null = null;
  private inFlightRemoteVersionPromise: Promise<string | null> | null = null;
  private remoteVersionCache: RemoteVersionCache | null = null;

  private async getLastRemoteCheckTimestamp(): Promise<number | null> {
    if (this.lastRemoteCheckCache !== null) {
      return this.lastRemoteCheckCache;
    }

    try {
      const stored = await AsyncStorage.getItem(LAST_REMOTE_CHECK_KEY);
      if (stored) {
        const timestamp = parseInt(stored, 10);
        this.lastRemoteCheckCache = timestamp;
        return timestamp;
      }
      return null;
    } catch (error) {
      console.error('🔄 [CACHE-VERSION] Error reading last remote check timestamp:', error);
      return null;
    }
  }

  private async setLastRemoteCheckTimestamp(): Promise<void> {
    const now = Date.now();
    try {
      await AsyncStorage.setItem(LAST_REMOTE_CHECK_KEY, now.toString());
      this.lastRemoteCheckCache = now;
      console.log(`🔄 [CACHE-VERSION] 💾 Stored last remote check timestamp: ${new Date(now).toISOString()}`);
    } catch (error) {
      console.error('🔄 [CACHE-VERSION] Error storing last remote check timestamp:', error);
    }
  }

  async shouldCheckRemoteVersion(forceCheck?: boolean): Promise<boolean> {
    if (forceCheck) {
      console.log('🔄 [CACHE-VERSION] Force check requested - will check remote version');
      return true;
    }

    const lastCheck = await this.getLastRemoteCheckTimestamp();

    if (lastCheck === null) {
      console.log('🔄 [CACHE-VERSION] No previous remote check found - will check remote version');
      return true;
    }

    const timeSinceLastCheck = Date.now() - lastCheck;
    const hoursSinceLastCheck = (timeSinceLastCheck / (1000 * 60 * 60)).toFixed(2);

    if (timeSinceLastCheck >= REMOTE_CHECK_INTERVAL_MS) {
      console.log(`🔄 [CACHE-VERSION] Last check was ${hoursSinceLastCheck}h ago (>= 24h) - will check remote version`);
      return true;
    }

    console.log(`🔄 [CACHE-VERSION] Last check was ${hoursSinceLastCheck}h ago (< 24h) - skipping remote check`);
    return false;
  }

  async getRemoteVersion(): Promise<string | null> {
    if (this.remoteVersionCache) {
      const age = Date.now() - this.remoteVersionCache.timestamp;
      if (age < REMOTE_VERSION_SHORT_TTL_MS) {
        console.log(`🔄 [CACHE-VERSION] ✅ Short-term cache hit (age: ${Math.round(age / 1000)}s)`);
        return this.remoteVersionCache.version;
      }
      console.log(`🔄 [CACHE-VERSION] Short-term cache expired (age: ${Math.round(age / 1000)}s)`);
    }

    if (this.inFlightRemoteVersionPromise) {
      console.log('🔄 [CACHE-VERSION] Request already in-flight, waiting for existing promise...');
      return this.inFlightRemoteVersionPromise;
    }

    this.inFlightRemoteVersionPromise = this.fetchRemoteVersionFromDatabase();

    try {
      const result = await this.inFlightRemoteVersionPromise;
      return result;
    } finally {
      this.inFlightRemoteVersionPromise = null;
    }
  }

  private async fetchRemoteVersionFromDatabase(): Promise<string | null> {
    const startTime = Date.now();
    console.log('🔄 [CACHE-VERSION] Fetching global remote version from database...');

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        console.log('⏱️ [CACHE-VERSION] Query timeout after 5000ms, aborting...');
        controller.abort();
      }, 5000);

      const { data, error } = await supabase
        .from('content_cache_versions')
        .select('timestamp_of_contents_table_update')
        .eq('id', 1)
        .maybeSingle()
        .abortSignal(controller.signal);

      clearTimeout(timeoutId);

      if (error) {
        console.error('🔄 [CACHE-VERSION] Error fetching remote version:', error);
        return null;
      }

      if (!data) {
        console.log('🔄 [CACHE-VERSION] No global version found');
        return null;
      }

      const duration = Date.now() - startTime;
      const timestamp = data.timestamp_of_contents_table_update;
      console.log(`🔄 [CACHE-VERSION] ✅ Remote version: ${timestamp} (${duration}ms)`);

      this.remoteVersionCache = {
        version: timestamp,
        timestamp: Date.now(),
      };

      return timestamp;
    } catch (err: any) {
      if (err?.name === 'AbortError' || err?.message?.includes('aborted')) {
        console.log('⏱️ [CACHE-VERSION] Query aborted due to timeout');
        return null;
      }
      console.error('🔄 [CACHE-VERSION] Unexpected error:', err);
      return null;
    }
  }

  async getLocalVersion(): Promise<string | null> {
    if (this.versionCache) {
      console.log('🔄 [CACHE-VERSION] ✅ Memory cache hit');
      return this.versionCache.timestamp;
    }

    try {
      const stored = await AsyncStorage.getItem(LOCAL_VERSION_KEY);
      if (stored) {
        const version: CacheVersion = JSON.parse(stored);
        this.versionCache = version;
        console.log(`🔄 [CACHE-VERSION] ✅ Local version: ${version.timestamp}`);
        return version.timestamp;
      }

      console.log('🔄 [CACHE-VERSION] No local version found');
      return null;
    } catch (error) {
      console.error('🔄 [CACHE-VERSION] Error reading local version:', error);
      return null;
    }
  }

  async setLocalVersion(timestamp: string): Promise<void> {
    const version: CacheVersion = {
      timestamp,
      localTimestamp: Date.now(),
    };

    try {
      await AsyncStorage.setItem(LOCAL_VERSION_KEY, JSON.stringify(version));
      this.versionCache = version;
      console.log(`🔄 [CACHE-VERSION] 💾 Stored local version: ${timestamp}`);
    } catch (error) {
      console.error('🔄 [CACHE-VERSION] Error storing local version:', error);
    }
  }

  async shouldInvalidateCache(forceCheck?: boolean): Promise<{
    shouldInvalidate: boolean;
    reason: string;
    remoteVersion: string | null;
    localVersion: string | null;
  }> {
    const startTime = Date.now();
    console.log('🔄 [CACHE-VERSION] ========================================');
    console.log(`🔄 [CACHE-VERSION] Checking if cache should be invalidated... (forceCheck: ${!!forceCheck})`);

    const shouldCheck = await this.shouldCheckRemoteVersion(forceCheck);
    if (!shouldCheck) {
      const duration = Date.now() - startTime;
      console.log('🔄 [CACHE-VERSION] ⏭️ Remote check skipped - last check was within 24 hours');
      console.log(`🔄 [CACHE-VERSION] ======================================== (${duration}ms)`);
      return {
        shouldInvalidate: false,
        reason: 'check_skipped_within_interval',
        remoteVersion: null,
        localVersion: await this.getLocalVersion(),
      };
    }

    const localVersion = await this.getLocalVersion();
    const remoteVersion = await this.getRemoteVersion();

    if (remoteVersion) {
      await this.setLastRemoteCheckTimestamp();
    }

    if (!remoteVersion) {
      const duration = Date.now() - startTime;
      console.log('🔄 [CACHE-VERSION] ⚠️ Could not fetch remote version, keeping cache');
      console.log(`🔄 [CACHE-VERSION] ======================================== (${duration}ms)`);
      return {
        shouldInvalidate: false,
        reason: 'remote_version_unavailable',
        remoteVersion: null,
        localVersion,
      };
    }

    if (!localVersion) {
      console.log('🔄 [CACHE-VERSION] ⚠️ No local version found');
      console.log('🔄 [CACHE-VERSION] Checking if cache contains data...');

      const hasData = await persistentContentCache.hasData();

      if (hasData) {
        const duration = Date.now() - startTime;
        console.log('🔄 [CACHE-VERSION] ❌ Cache contains data but no version tracking!');
        console.log('🔄 [CACHE-VERSION]   - This indicates lost version tracking');
        console.log('🔄 [CACHE-VERSION]   - Remote version: ' + remoteVersion);
        console.log('🔄 [CACHE-VERSION]   - Action: INVALIDATE and REFRESH');
        console.log(`🔄 [CACHE-VERSION] ======================================== (${duration}ms)`);

        return {
          shouldInvalidate: true,
          reason: 'lost_version_tracking',
          remoteVersion,
          localVersion: null,
        };
      }

      const duration = Date.now() - startTime;
      console.log('🔄 [CACHE-VERSION] ✅ No local version, first run - will fetch and cache');
      console.log(`🔄 [CACHE-VERSION] ======================================== (${duration}ms)`);
      return {
        shouldInvalidate: false,
        reason: 'first_run',
        remoteVersion,
        localVersion: null,
      };
    }

    const localDate = new Date(localVersion);
    const remoteDate = new Date(remoteVersion);

    if (remoteDate > localDate) {
      const timeDiff = remoteDate.getTime() - localDate.getTime();
      const minutesDiff = Math.round(timeDiff / 60000);
      const duration = Date.now() - startTime;

      console.log('🔄 [CACHE-VERSION] ❌ Cache is OUTDATED');
      console.log(`🔄 [CACHE-VERSION]   - Local version:  ${localVersion}`);
      console.log(`🔄 [CACHE-VERSION]   - Remote version: ${remoteVersion}`);
      console.log(`🔄 [CACHE-VERSION]   - Time difference: ${minutesDiff} minutes`);
      console.log(`🔄 [CACHE-VERSION]   - Action: INVALIDATE and REFRESH`);
      console.log(`🔄 [CACHE-VERSION] ======================================== (${duration}ms)`);

      return {
        shouldInvalidate: true,
        reason: 'outdated',
        remoteVersion,
        localVersion,
      };
    }

    const duration = Date.now() - startTime;
    console.log('🔄 [CACHE-VERSION] ✅ Cache version is UP TO DATE');
    console.log(`🔄 [CACHE-VERSION]   - Local version:  ${localVersion}`);
    console.log(`🔄 [CACHE-VERSION]   - Remote version: ${remoteVersion}`);

    // Check if cache actually contains data
    console.log('🔄 [CACHE-VERSION] Verifying cache data presence...');
    const hasData = await persistentContentCache.hasData();

    if (!hasData) {
      console.log('🔄 [CACHE-VERSION] ⚠️ Cache version is up-to-date but cache is EMPTY');
      console.log('🔄 [CACHE-VERSION]   - Action: INVALIDATE and FETCH from Supabase');
      console.log(`🔄 [CACHE-VERSION] ======================================== (${Date.now() - startTime}ms)`);

      return {
        shouldInvalidate: true,
        reason: 'cache_empty',
        remoteVersion,
        localVersion,
      };
    }

    console.log('🔄 [CACHE-VERSION]   - Action: USE CACHE');
    console.log(`🔄 [CACHE-VERSION] ======================================== (${duration}ms)`);

    return {
      shouldInvalidate: false,
      reason: 'up_to_date',
      remoteVersion,
      localVersion,
    };
  }

  async clearLocalVersion(): Promise<void> {
    try {
      await AsyncStorage.removeItem(LOCAL_VERSION_KEY);
      this.versionCache = null;
      this.remoteVersionCache = null;
      console.log('🔄 [CACHE-VERSION] 🗑️ Cleared local version and short-term cache');
    } catch (error) {
      console.error('🔄 [CACHE-VERSION] Error clearing local version:', error);
    }
  }

  clearRemoteVersionCache(): void {
    this.remoteVersionCache = null;
    console.log('🔄 [CACHE-VERSION] 🗑️ Cleared short-term remote version cache');
  }

  async forceUpdateRemoteVersion(): Promise<boolean> {
    console.log('🔄 [CACHE-VERSION] Force updating remote version...');

    try {
      const { error } = await supabase
        .from('content_cache_versions')
        .update({
          timestamp_of_contents_table_update: new Date().toISOString(),
        })
        .eq('id', 1);

      if (error) {
        console.error('🔄 [CACHE-VERSION] Error updating remote version:', error);
        return false;
      }

      console.log('🔄 [CACHE-VERSION] ✅ Remote version updated successfully');
      return true;
    } catch (error) {
      console.error('🔄 [CACHE-VERSION] Unexpected error updating remote version:', error);
      return false;
    }
  }

  async getCacheStats(): Promise<{
    hasLocalVersion: boolean;
    localVersion: string | null;
    age: number | null;
  }> {
    try {
      const stored = await AsyncStorage.getItem(LOCAL_VERSION_KEY);
      if (stored) {
        const version: CacheVersion = JSON.parse(stored);
        const age = Date.now() - version.localTimestamp;
        return {
          hasLocalVersion: true,
          localVersion: version.timestamp,
          age: Math.round(age / 60000),
        };
      }

      return {
        hasLocalVersion: false,
        localVersion: null,
        age: null,
      };
    } catch (error) {
      console.error('🔄 [CACHE-VERSION] Error getting cache stats:', error);
      return {
        hasLocalVersion: false,
        localVersion: null,
        age: null,
      };
    }
  }
}

export const cacheVersionService = new CacheVersionService();
